import { Calendar } from './Calendar';
import Constants from './Constants';
import DateUtils from './DateUtils';
import FeedUtils from './FeedUtils';
import { FeedItemSubType } from '../models/Feed';
import { FileType } from '../models/File';
import { CommonFileUtils } from './CommonFileUtils';
import { TimelineUtils } from './TimelineUtils';
export class CommonSubmittalUtils {
    static COVER_PAGE_ID = 'cover';
    static FEED_PAGE_ID = 'comments';
    static PAGE_WIDTH = CommonFileUtils.PAGE_WIDTH;
    static PAGE_HEIGHT = CommonFileUtils.PAGE_HEIGHT;
    static PAGE_HEADER_HEIGHT = CommonFileUtils.PAGE_HEADER_HEIGHT;
    static BODY_Y = CommonFileUtils.BODY_Y;
    static MARGIN = 48;
    static COLUMN_GAP = 24;
    static FONT = 'Arial, Helvetica, sans-serif';
    static BODY_SIZE = 12;
    static TITLE_SIZE = 22;
    static LINE_HEIGHT = 20;
    static LABEL_WIDTH = 148;
    static SECTION_GAP = 14;
    static TABLE_HEADER_HEIGHT = 24;
    static TABLE_ROW_HEIGHT = 24;
    static FEED_META_SIZE = 8.5;
    static FEED_TEXT_SIZE = 12;
    static FEED_ROW_MIN_HEIGHT = 56;
    static ATTACHMENT_ROW_HEIGHT = 52;
    static ATTACHMENT_THUMB_SIZE = 36;
    static MAX_ATTACHMENT_ROWS = 6;
    static DEFAULT_SUBMITTAL_TYPES = [
        'Shop Drawing',
        'Product Data',
        'Sample',
        'Document',
        'Test Report',
        'Closeout',
        'Other',
    ];
    static DEFAULT_SUBMITTAL_WORKFLOW = {
        id: 'default',
        name: 'Default Submittal Workflow',
        states: [
            {
                id: 'create',
                pastTense: 'Created',
                action: 'create',
                dueDays: 0,
                dueBy: [],
            },
            {
                id: 'submit',
                pastTense: 'Submitted',
                action: 'Submit',
                dueDays: 10,
                dueBy: [],
            },
            {
                id: 'approve',
                pastTense: 'Approved',
                action: 'Approve',
                dueDays: 5,
                dueBy: [],
            },
        ],
    };
    static TABLE_COLUMNS = [
        { key: 'number', label: 'Number' },
        { key: 'title', label: 'Title' },
        { key: 'createdBy', label: 'Created By' },
        { key: 'createdDate', label: 'Created Date' },
        { key: 'division', label: 'Division' },
        { key: 'specSection', label: 'Spec Section' },
        { key: 'submittalType', label: 'Type' },
        { key: 'packageTitle', label: 'Package' },
        { key: 'status', label: 'Status' },
        { key: 'assignee', label: 'Ball in court' },
        { key: 'statusDueDate', label: 'Ball in court Due Date' },
        { key: 'dueDate', label: 'Due Date' },
        { key: 'workflowDueDate', label: 'Workflow Due Date' },
        { key: 'lateDays', label: 'Late Days' },
    ];
    static getDefaultSubmittalSettingsDoc() {
        return {
            _id: 'settings',
            workflows: [
                {
                    ...CommonSubmittalUtils.DEFAULT_SUBMITTAL_WORKFLOW,
                    states: CommonSubmittalUtils.DEFAULT_SUBMITTAL_WORKFLOW.states.map((s) => ({ ...s, dueBy: [...s.dueBy] })),
                },
            ],
            types: [...CommonSubmittalUtils.DEFAULT_SUBMITTAL_TYPES],
            packages: [],
            columns: CommonSubmittalUtils.TABLE_COLUMNS.map((col) => col.key),
        };
    }
    static getDbName(projectId) {
        return `p-${projectId}-submittals`;
    }
    /** Original creator: doc field, else first revision (drafts often only have revision metadata). */
    static getCreatedById(submittal) {
        return submittal.created_by || this.getLatestRevision(submittal)?.created_by || '';
    }
    static getCreatedDateIso(submittal) {
        return submittal.created_date || this.getLatestRevision(submittal)?.created_date || '';
    }
    static getCreatedByName(submittal, people) {
        const personId = this.getCreatedById(submittal);
        if (!personId)
            return '';
        const person = this.findPerson(people, personId);
        return person ? this.getFullName(person) : '';
    }
    static getFeedDbName(projectId, submittalId) {
        return `p-${projectId}-submittal-${submittalId}-feed`;
    }
    static getMarkupsDbName(projectId, submittalId) {
        return `p-${projectId}-submittal-${submittalId}-markups`;
    }
    static getLatestRevision(submittal) {
        if (!submittal.revisions || submittal.revisions.length === 0)
            return null;
        return submittal.revisions[submittal.revisions.length - 1];
    }
    static setLatestRevision(submittal, revision) {
        if (!submittal.revisions || submittal.revisions.length === 0) {
            submittal.revisions = [revision];
        }
        else {
            submittal.revisions[submittal.revisions.length - 1] = revision;
        }
        return submittal;
    }
    static isDraft(submittal) {
        const revision = this.getLatestRevision(submittal);
        return revision ? this.isDraftRevision(revision) : false;
    }
    static isDraftRevision(revision) {
        return revision.completed_state_index == undefined;
    }
    static isFinalState(revision) {
        return revision.completed_state_index == revision.states.length - 2;
    }
    static isComplete(submittal) {
        const revision = this.getLatestRevision(submittal);
        return revision ? this.isRevisionCompleted(revision) : false;
    }
    static isRevisionCompleted(revision) {
        return revision.completed_state_index == revision.states.length - 1;
    }
    static filterRevisionFeed(feed, revision) {
        return feed.filter((item) => item.revision === revision);
    }
    static filterRevisionMarkups(markups, revision) {
        return markups.filter((markup) => markup.revision === revision);
    }
    static filterRevisionFiles(files, feed) {
        const fileIds = feed
            .filter((item) => item.sub === FeedItemSubType.FILE && !item.deleted_date)
            .map((item) => item.sub_id);
        return files.filter((file) => fileIds.includes(file._id));
    }
    /** Feed items included in the submittal (not excluded or deleted). */
    static filterIncludedFeedItems(feed) {
        return feed.filter((item) => !item.excluded && !item.deleted_by && !item.deleted_date);
    }
    static FILE_LIKE_FEED_SUBS = new Set([
        FeedItemSubType.FILE,
        FeedItemSubType.PHOTO,
        FeedItemSubType.DRAWING,
        FeedItemSubType.SPECIFICATION,
    ]);
    /** File ids for included file-like feed items, in feed order. */
    static getIncludedFileIds(feed) {
        const fileIds = [];
        const seen = new Set();
        for (const item of this.filterIncludedFeedItems(feed)) {
            if (!item?.sub_id || !this.FILE_LIKE_FEED_SUBS.has(item.sub) || seen.has(item.sub_id))
                continue;
            seen.add(item.sub_id);
            fileIds.push(item.sub_id);
        }
        return fileIds;
    }
    /** FileDocs for included file-like feed items, in feed order. */
    static getIncludedFileDocs(feed, files) {
        return this.getIncludedFileIds(feed)
            .map((id) => files.find((file) => file._id === id))
            .filter((file) => !!file && !file.deleted_by);
    }
    /** Resolve FileDocs for included file-like feed items (document viewer / PDF cover). */
    static filterIncludedFiles(includedFeedItems, files) {
        const fileIds = Array.from(new Set(includedFeedItems
            .filter((item) => this.FILE_LIKE_FEED_SUBS.has(item.sub) && item.sub_id)
            .map((item) => item.sub_id)));
        return fileIds
            .map((id) => files.find((file) => file._id === id))
            .filter((file) => !!file && !file.deleted_by);
    }
    static getCompletedState(revision) {
        if (revision.completed_state_index === undefined || !revision.states || revision.states.length === 0)
            return null;
        return revision.completed_state_index >= 0 && revision.completed_state_index < revision.states.length - 1
            ? revision.states[revision.completed_state_index]
            : null;
    }
    static getPendingState(revision) {
        if (!revision.states || revision.states.length === 0)
            return null;
        if (revision.completed_state_index === undefined)
            return revision.states[0];
        return revision.completed_state_index >= 0 && revision.completed_state_index < revision.states.length - 1
            ? revision.states[revision.completed_state_index + 1]
            : null;
    }
    static getNextState(revision) {
        if (!revision.states || revision.states.length < 1)
            return null;
        if (revision.completed_state_index === undefined)
            return revision.states[1];
        return revision.completed_state_index >= 0 && revision.completed_state_index < revision.states.length - 2
            ? revision.states[revision.completed_state_index + 2]
            : null;
    }
    static getStateDueDate(state, previousDoneDate, calendar) {
        if (!state || !state.dueDays || state.dueDays <= 0)
            return previousDoneDate;
        const durationInSeconds = calendar.workingDaysToSeconds(state.dueDays);
        return calendar.getDateFromPositiveDuration(previousDoneDate, durationInSeconds);
    }
    static getWorkflowDueDate(submittal, calendar) {
        const revision = this.getLatestRevision(submittal);
        if (!revision?.completed_state_date || revision.completed_state_index == null)
            return null;
        let referenceDate = DateUtils.fromISO(revision.completed_state_date);
        return TimelineUtils.getWorkflowDueDate(revision.states, revision.completed_state_index, referenceDate, calendar);
    }
    static getFinishDate(submittal, calendar) {
        if (submittal.finish_date)
            return DateUtils.fromISO(submittal.finish_date);
        const workflowDueTime = this.getWorkflowDueDate(submittal, calendar)?.getTime() || 0;
        const dueTime = DateUtils.fromISO(submittal.due_date)?.getTime() || 0;
        return new Date(Math.max(workflowDueTime, dueTime));
    }
    /*
    static getRevisionCompletedStateId(revision: SubmittalRevision, feed: SubmittalFeedItemDoc[]): string | null {
      if (!revision.status_history || revision.status_history.length === 0) return null;
      const statusHistory = this.filterStatusFeedItems(feed, revision);
      const lastStateId =
        statusHistory && statusHistory.length > 0 ? statusHistory[statusHistory.length - 1].state_id : null;
      // Null state_id means the submittal has been moved back to the initial "created" state
      return lastStateId ?? null;
    }
  
    static getRevisionStateIndex(submittal: SubmittalDoc, revision: SubmittalRevision, feed: SubmittalFeedItemDoc[]): number {
      const statusHistory = this.filterStatusFeedItems(feed, revision);
      if (!statusHistory || statusHistory.length === 0) {
        return 0; // No history, at first state (created)
      }
  
      // Find the last non-null state_id in history
      let lastCompletedStateId = null;
      let consecutiveNulls = 0;
  
      for (let i = statusHistory.length - 1; i >= 0; i--) {
        if (statusHistory[i].state_id === null) {
          consecutiveNulls++;
        } else {
          lastCompletedStateId = statusHistory[i].state_id;
          break;
        }
      }
  
      // If all entries are null, we're at first state
      if (lastCompletedStateId === null) {
        return 0;
      }
  
      // Find where that completed state is in the workflow
      const completedStateIndex = submittal.states.findIndex((state) => state.id === lastCompletedStateId);
  
      // Current state = one after completed state, minus the number of backward steps (nulls)
      const currentStateIndex = completedStateIndex + 1 - consecutiveNulls;
  
      return Math.max(0, currentStateIndex); // Don't go below 0
    }
      */
    static filterStatusFeedItems(feed, revision) {
        return feed.filter((item) => item.sub === FeedItemSubType.STATUS && item.revision === revision.num);
    }
    static getPreviousStatusFeedItem(feed, submittal) {
        const revision = this.getLatestRevision(submittal);
        if (!revision)
            return null;
        return this.filterStatusFeedItems(feed, revision).sort(FeedUtils.sortNewestFirst).at(0) || null;
    }
    static get columnWidth() {
        return Math.floor((this.PAGE_WIDTH - this.MARGIN * 2 - this.COLUMN_GAP) / 2);
    }
    static get rightColumnX() {
        return this.MARGIN + this.columnWidth + this.COLUMN_GAP;
    }
    static getCoverPage(options) {
        const { submittal, people, project, company, feed = [], files = [] } = options;
        const revision = this.getLatestRevision(options.submittal);
        if (!revision) {
            throw new Error('Revision not found');
        }
        const currentState = this.getPendingState(revision);
        const leftFields = this.getLeftColumnFields(submittal, revision, currentState, people, options);
        const rightFields = this.getRightColumnFields(submittal, revision, people, options.manager, feed, options.costCodeDescription);
        const fullWidthFields = this.getFullWidthFields(submittal, revision, currentState, people);
        const leftColumnSvg = CommonFileUtils.renderFieldColumn(leftFields, this.MARGIN, CommonFileUtils.BODY_Y, this.columnWidth, {
            labelWidth: this.LABEL_WIDTH,
            multilineGap: 6,
        });
        const rightColumnSvg = CommonFileUtils.renderFieldColumn(rightFields, this.rightColumnX, CommonFileUtils.BODY_Y, this.columnWidth, {
            labelWidth: this.LABEL_WIDTH,
            multilineGap: 6,
        });
        const fullWidthSvg = CommonFileUtils.renderFieldColumn(fullWidthFields, this.MARGIN, Math.max(leftColumnSvg.endY, rightColumnSvg.endY) + 8, this.PAGE_WIDTH - this.MARGIN * 2, { labelWidth: this.LABEL_WIDTH, multilineGap: 6 });
        const attachmentsSection = this.renderAttachmentsSection(options.files || [], this.MARGIN, fullWidthSvg.endY + this.SECTION_GAP + 8, this.PAGE_WIDTH - this.MARGIN * 2);
        const workflowSection = this.renderWorkflowSection(submittal, revision, people, feed, this.MARGIN, attachmentsSection.endY + this.SECTION_GAP + 8, this.PAGE_WIDTH - this.MARGIN * 2, options.calendar ?? Calendar.getDefaultCalendar());
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${this.PAGE_WIDTH}" height="${this.PAGE_HEIGHT}" viewBox="0 0 ${this.PAGE_WIDTH} ${this.PAGE_HEIGHT}" style="display:block">
  <rect width="100%" height="100%" fill="#ffffff"/>
  ${CommonFileUtils.renderHeader(company, project)}
  ${CommonFileUtils.renderTitle(this.escapeXml(`Submittal #${submittal.code}: ${submittal.title ?? ''}`))}
  ${leftColumnSvg.svg}
  ${rightColumnSvg.svg}
  ${fullWidthSvg.svg}
  ${attachmentsSection.svg}
  ${workflowSection.svg}
  ${CommonFileUtils.renderFooter(company, 1, 2)}
</svg>`;
        return {
            _id: this.COVER_PAGE_ID,
            file_id: submittal._id,
            title: 'Cover',
            thumb_width: this.PAGE_WIDTH,
            thumb_height: this.PAGE_HEIGHT,
            body: svg,
        };
    }
    static getLeftColumnFields(submittal, revision, currentState, people, options) {
        const createdDate = DateUtils.fromISO(revision?.created_date || submittal.created_date);
        const createdBy = revision?.created_by || submittal.created_by;
        const creator = createdBy ? this.findPerson(people, createdBy) : null;
        const responsibleContractor = creator?.company || creator?.first_name + ' ' + creator?.last_name;
        return [
            { label: 'Revision', value: String(revision?.num ?? 0) },
            { label: 'Status', value: this.getStatusLabel(currentState) },
            { label: 'Issue Date', value: createdDate ? DateUtils.format(createdDate) : '' },
            { label: 'Responsible Contractor', value: responsibleContractor },
            { label: 'Due Date', value: submittal.due_date ? DateUtils.formatIso(submittal.due_date) : '' },
        ];
    }
    static getRightColumnFields(submittal, revision, people, manager, feed, costCodeDescription) {
        const specSection = submittal.spec_section;
        const costCode = submittal.cost_code;
        const createdDate = revision?.created_date || submittal.created_date;
        const statusHistory = feed.filter((item) => item.sub === FeedItemSubType.STATUS);
        const lastSubmitItem = statusHistory.find((item) => item.sub_id === Constants.SUBMITTAL_SUBMIT_STATUS_ID);
        return [
            {
                label: 'Submittal Manager',
                value: manager ? this.formatPersonWithCompany(people, manager) : '',
            },
            { label: 'Date Created', value: createdDate ? DateUtils.formatIso(createdDate) : '' },
            {
                label: 'Spec Section',
                value: specSection ? [specSection.num, specSection.title].filter(Boolean).join(' - ') : '',
            },
            {
                label: 'Submit By',
                value: lastSubmitItem?.user_id
                    ? this.formatPersonWithCompany(people, lastSubmitItem?.user_id)
                    : 'Not Submitted',
            },
            {
                label: 'Cost Code',
                value: [costCode, costCodeDescription].filter(Boolean).join(' - '),
            },
            { label: 'Type', value: submittal.type ?? '' },
        ];
    }
    static getFullWidthFields(submittal, revision, currentState, people) {
        const lastState = revision ? revision.states?.[revision.states.length - 1] : null;
        return [
            { label: 'Submittal Package', value: submittal.package?.title ?? '' },
            { label: 'Approvers', value: this.formatPeopleList(people, lastState?.dueBy) },
            { label: 'Ball in Court', value: this.formatPeopleList(people, currentState?.dueBy) },
            { label: 'Distribution', value: this.formatPeopleList(people, submittal.distribution) },
            { label: 'Description', value: submittal.description ?? '', multiline: true },
        ];
    }
    static getStatusLabel(currentState) {
        return currentState?.action || 'Open';
    }
    /** User-facing submittal number; falls back to legacy `number` field. */
    static getSubmittalCode(submittal) {
        return submittal?.code || submittal?.number || submittal?._id || '';
    }
    static renderWorkflowSection(submittal, revision, people, feed, x, y, width, calendar) {
        const titleY = y;
        const tableY = titleY + 16;
        const columnWidths = [0.18, 0.2, 0.16, 0.18, 0.28].map((ratio) => Math.round(width * ratio));
        const totalWidth = columnWidths.reduce((sum, col) => sum + col, 0);
        const adjustedColumnWidths = [...columnWidths];
        adjustedColumnWidths[adjustedColumnWidths.length - 1] += width - totalWidth;
        const headers = ['Action', 'By', 'Due Date', 'Completed Date', 'Comment'];
        const rows = this.getWorkflowRows(submittal, revision, people, feed, calendar);
        const bodyHeight = rows.length * this.TABLE_ROW_HEIGHT;
        const tableHeight = this.TABLE_HEADER_HEIGHT + bodyHeight;
        let cursorX = x;
        const separators = [];
        for (let i = 0; i < adjustedColumnWidths.length - 1; i++) {
            cursorX += adjustedColumnWidths[i];
            separators.push(`<line x1="${cursorX}" y1="${tableY}" x2="${cursorX}" y2="${tableY + tableHeight}" stroke="#d0d0d0" stroke-width="1"/>`);
        }
        const horizontalLines = [
            `<line x1="${x}" y1="${tableY + this.TABLE_HEADER_HEIGHT}" x2="${x + width}" y2="${tableY + this.TABLE_HEADER_HEIGHT}" stroke="#d0d0d0" stroke-width="1"/>`,
        ];
        for (let i = 1; i < rows.length; i++) {
            const lineY = tableY + this.TABLE_HEADER_HEIGHT + i * this.TABLE_ROW_HEIGHT;
            horizontalLines.push(`<line x1="${x}" y1="${lineY}" x2="${x + width}" y2="${lineY}" stroke="#ebebeb" stroke-width="1"/>`);
        }
        const headerTexts = headers
            .map((header, index) => {
            const cellStart = x + adjustedColumnWidths.slice(0, index).reduce((sum, col) => sum + col, 0);
            return `<text x="${cellStart + 6}" y="${tableY + 16}" font-family="${this.FONT}" font-size="10.5" font-weight="bold" fill="#333333">${this.escapeXml(header)}</text>`;
        })
            .join('\n  ');
        const rowTexts = rows
            .map((row, rowIndex) => {
            const rowY = tableY + this.TABLE_HEADER_HEIGHT + rowIndex * this.TABLE_ROW_HEIGHT + 16;
            return row
                .map((value, colIndex) => {
                const cellStart = x + adjustedColumnWidths.slice(0, colIndex).reduce((sum, col) => sum + col, 0);
                const maxChars = Math.max(8, Math.floor((adjustedColumnWidths[colIndex] - 12) / 5.5));
                return `<text x="${cellStart + 6}" y="${rowY}" font-family="${this.FONT}" font-size="10.5" fill="#222222">${this.escapeXml(this.truncateText(value, maxChars))}</text>`;
            })
                .join('\n  ');
        })
            .join('\n  ');
        return {
            svg: `<line x1="${x}" y1="${titleY + 20}" x2="${x + width}" y2="${titleY + 20}" stroke="#d8d8d8" stroke-width="1"/>
  <text x="${x}" y="${titleY}" font-family="${this.FONT}" font-size="19" font-weight="bold" fill="#111111">Submittal Workflow</text>
  <rect x="${x}" y="${tableY}" width="${width}" height="${tableHeight}" fill="#ffffff" stroke="#d0d0d0" stroke-width="1"/>
  <rect x="${x}" y="${tableY}" width="${width}" height="${this.TABLE_HEADER_HEIGHT}" fill="#f0f0f0"/>
  ${headerTexts}
  ${rowTexts}
  ${separators.join('\n  ')}
  ${horizontalLines.join('\n  ')}`,
            endY: tableY + tableHeight,
        };
    }
    static renderAttachmentsSection(files, x, y, width) {
        const titleY = y;
        const sectionY = titleY + 12;
        if (!files.length) {
            return {
                svg: `<line x1="${x}" y1="${titleY + 20}" x2="${x + width}" y2="${titleY + 20}" stroke="#d8d8d8" stroke-width="1"/>
  <text x="${x}" y="${titleY}" font-family="${this.FONT}" font-size="15" font-weight="bold" fill="#111111">Attachments</text>
  <text x="${x + 8}" y="${sectionY + 22}" font-family="${this.FONT}" font-size="11" fill="#5a5a5a">No attachments included in this revision.</text>`,
                endY: sectionY + 30,
            };
        }
        const visibleFiles = files.filter((file) => file.type != FileType.PHOTO).slice(0, this.MAX_ATTACHMENT_ROWS);
        const hiddenCount = files.length - visibleFiles.length;
        const rowParts = visibleFiles.map((file, index) => {
            const rowY = sectionY + index * this.ATTACHMENT_ROW_HEIGHT;
            const thumbX = x + 8;
            const thumbY = rowY + 8;
            const thumbSvg = this.renderAttachmentThumbnail(file, thumbX, thumbY);
            const name = this.truncateText(file.name || 'Untitled file', 76);
            const sizeLabel = typeof file.num_bytes === 'number' && file.num_bytes > 0
                ? `${Math.max(1, Math.round(file.num_bytes / 1024))} KB`
                : '';
            const meta = [file.type, sizeLabel].filter(Boolean).join(' • ');
            return `<rect x="${x}" y="${rowY}" width="${width}" height="${this.ATTACHMENT_ROW_HEIGHT}" fill="#ffffff" stroke="#e3e3e3" stroke-width="1"/>
  ${thumbSvg}
  <text x="${thumbX + this.ATTACHMENT_THUMB_SIZE + 10}" y="${rowY + 24}" font-family="${this.FONT}" font-size="11.5" font-weight="bold" fill="#222222">${this.escapeXml(name)}</text>
  <text x="${thumbX + this.ATTACHMENT_THUMB_SIZE + 10}" y="${rowY + 40}" font-family="${this.FONT}" font-size="10" fill="#606060">${this.escapeXml(meta)}</text>`;
        });
        const hiddenFilesLabel = hiddenCount > 0
            ? `<text x="${x + 8}" y="${sectionY + visibleFiles.length * this.ATTACHMENT_ROW_HEIGHT + 18}" font-family="${this.FONT}" font-size="10.5" fill="#5a5a5a">+${hiddenCount} more attachment${hiddenCount === 1 ? '' : 's'}</text>`
            : '';
        const sectionHeight = visibleFiles.length * this.ATTACHMENT_ROW_HEIGHT + (hiddenCount > 0 ? 24 : 0);
        return {
            svg: `<line x1="${x}" y1="${titleY + 20}" x2="${x + width}" y2="${titleY + 20}" stroke="#d8d8d8" stroke-width="1"/>
  <text x="${x}" y="${titleY}" font-family="${this.FONT}" font-size="15" font-weight="bold" fill="#111111">Attachments</text>
  ${rowParts.join('\n  ')}
  ${hiddenFilesLabel}`,
            endY: sectionY + sectionHeight,
        };
    }
    static getWorkflowRows(submittal, revision, people, feed, calendar) {
        const revisionNum = revision?.num;
        const statusItemsAsc = this.filterStatusFeedItems(feed, revision)
            .filter((item) => !item.deleted_by &&
            !item.deleted_date &&
            (revisionNum === undefined || revisionNum === null || item.revision === revisionNum))
            .sort(FeedUtils.sortOldestFirst);
        const completedRows = statusItemsAsc.map((item) => {
            const stateId = item.sub_id ?? null;
            const stateIndex = revision.states.findIndex((state) => state.id === stateId);
            const state = stateIndex >= 0 ? revision.states[stateIndex] : null;
            const doneDate = DateUtils.fromISO(item.date);
            const dueDate = TimelineUtils.getStateDueDate(state, doneDate, calendar);
            const action = state?.pastTense ||
                item.data?.pastTense ||
                (typeof item.data?.action === 'string' ? item.data.action : '') ||
                'Completed';
            const person = this.findPerson(people, item.user_id);
            const personName = person ? this.getFullName(person) : 'Unknown User';
            const completeDate = DateUtils.formatIso(item.date) || '';
            return [action, personName, DateUtils.format(dueDate), completeDate, item.text || ''];
        });
        // Pending states (current + remaining): Action, By (dueBy), Due Date; Completed Date and Comment empty.
        const completedIndex = revision.completed_state_index ?? -1;
        const pendingStates = (revision.states || []).slice(completedIndex + 1);
        if (!pendingStates.length) {
            return completedRows;
        }
        const lastStatusItem = statusItemsAsc[statusItemsAsc.length - 1];
        let anchorDate = lastStatusItem ? DateUtils.fromISO(lastStatusItem.date) : DateUtils.currentDate;
        const pendingRows = pendingStates.map((state, index) => {
            const dueDate = this.getStateDueDate(state, anchorDate, calendar);
            if (index === 0) {
                // Floor anchor to now so subsequent pending dues don't chain from a past date.
                anchorDate = new Date(Math.max(dueDate.getTime(), DateUtils.currentDate.getTime()));
            }
            else {
                anchorDate = dueDate;
            }
            const dueByNames = (state.dueBy || [])
                .map((id) => this.findPerson(people, id))
                .filter((person) => !!person)
                .map((person) => this.getFullName(person))
                .join(', ');
            return [state.action || '', dueByNames, DateUtils.format(dueDate) || '', '', ''];
        });
        return [...completedRows, ...pendingRows];
    }
    static getFeedPage(submittal, people, project, company, feed, files = []) {
        const x = this.MARGIN;
        const width = this.PAGE_WIDTH - this.MARGIN * 2;
        const feedTitleY = this.BODY_Y;
        const tableY = feedTitleY + 12;
        const visibleFeed = (feed || [])
            .filter((item) => !item.deleted_by && !item.deleted_date && !item.excluded)
            .sort(FeedUtils.sortOldestFirst);
        const commentEntries = this.getRenderableCommentEntries(visibleFeed.filter((item) => item.sub === FeedItemSubType.COMMENT), people);
        const photoEntries = this.getRenderablePhotoEntries(visibleFeed.filter((item) => item.sub === FeedItemSubType.PHOTO), people, files);
        let contentY = tableY + 10;
        const rowParts = [];
        for (let i = 0; i < commentEntries.length; i++) {
            const entry = commentEntries[i];
            const metaLines = this.wrapText(entry.meta, width - 20, this.FEED_META_SIZE);
            const textLines = this.wrapText(entry.text, width - 20, this.FEED_TEXT_SIZE).slice(0, 4);
            const metaHeight = Math.max(1, metaLines.length) * 11;
            const textHeight = Math.max(1, textLines.length) * 16;
            const rowHeight = Math.max(this.FEED_ROW_MIN_HEIGHT, 8 + metaHeight + textHeight + 8);
            const metaSvg = metaLines
                .map((line, index) => {
                const lineY = contentY + 11 + index * 11;
                return `<text x="${x + 8}" y="${lineY}" font-family="${this.FONT}" font-size="${this.FEED_META_SIZE}" fill="#4b4b4b">${this.escapeXml(line)}</text>`;
            })
                .join('\n  ');
            const textBaseY = contentY + 11 + metaHeight + 4;
            const textSvg = textLines
                .map((line, index) => {
                const lineY = textBaseY + index * 16;
                return `<text x="${x + 8}" y="${lineY}" font-family="${this.FONT}" font-size="${this.FEED_TEXT_SIZE}" fill="#222222">${this.escapeXml(line)}</text>`;
            })
                .join('\n  ');
            const separator = i < commentEntries.length - 1
                ? `<line x1="${x}" y1="${contentY + rowHeight}" x2="${x + width}" y2="${contentY + rowHeight}" stroke="#b8b8b8" stroke-width="1"/>`
                : '';
            rowParts.push(`${metaSvg}
  ${textSvg}
  ${separator}`);
            contentY += rowHeight;
        }
        const tableHeight = Math.max(this.FEED_ROW_MIN_HEIGHT, contentY - tableY);
        const commentsSvg = `<rect x="${x}" y="${tableY}" width="${width}" height="${tableHeight}" fill="#ffffff" stroke="#6f6f6f" stroke-width="1"/>
  ${rowParts.join('\n  ')}`;
        const photosStartY = tableY + tableHeight + this.SECTION_GAP;
        const photosSvg = this.renderFeedPhotosHorizontal(photoEntries, x, photosStartY, width);
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${this.PAGE_WIDTH}" height="${this.PAGE_HEIGHT}" viewBox="0 0 ${this.PAGE_WIDTH} ${this.PAGE_HEIGHT}" style="display:block">
  <rect width="100%" height="100%" fill="#ffffff"/>
  <rect x="1" y="1" width="${this.PAGE_WIDTH - 2}" height="${this.PAGE_HEIGHT - 2}" fill="none" stroke="#222222" stroke-width="1"/>
  ${CommonFileUtils.renderHeader(company, project)}
  ${CommonFileUtils.renderTitle('Comments')}
  ${commentsSvg}
  ${photosSvg}
  ${CommonFileUtils.renderFooter(company, 2, 2)}
</svg>`;
        return {
            _id: this.FEED_PAGE_ID,
            file_id: submittal._id,
            title: 'Comments',
            thumb_width: this.PAGE_WIDTH,
            thumb_height: this.PAGE_HEIGHT,
            body: svg,
        };
    }
    static getRenderableCommentEntries(feed, people) {
        if (!feed.length) {
            return [{ meta: 'No comments', text: 'No comments for this submittal.' }];
        }
        return feed.map((item) => {
            const person = this.findPerson(people, item.user_id);
            const personName = person ? this.getFullName(person) : 'Unknown User';
            const company = person?.company ? `, ${person.company}` : '';
            const date = item.date ? DateUtils.formatIso(item.date) : '';
            const meta = [personName + company, date].filter(Boolean).join('  •  ');
            return { meta, text: item.text || '' };
        });
    }
    static getRenderablePhotoEntries(feed, people, files) {
        return feed
            .map((item) => {
            if (!item.sub_id)
                return null;
            const file = files.find((f) => f._id === item.sub_id);
            if (!file?.thumb || !file.thumb_width || !file.thumb_height)
                return null;
            const creator = this.findPerson(people, file.created_by || '');
            const creatorName = creator ? this.getFullName(creator) : 'Unknown User';
            const createdDate = file.created_date ? DateUtils.formatIso(file.created_date) : '';
            return {
                thumb: file.thumb,
                width: file.thumb_width,
                height: file.thumb_height,
                creatorName,
                createdDate,
            };
        })
            .filter((entry) => !!entry);
    }
    /** Horizontal photo strip after the comments table. */
    static renderFeedPhotosHorizontal(photos, x, startY, maxWidth) {
        if (!photos.length) {
            return '';
        }
        const gap = 12;
        const captionLineHeight = 14;
        const captionBlockHeight = captionLineHeight * 2 + 6;
        let cursorX = x;
        let rowY = startY;
        let rowMaxHeight = 0;
        const parts = [];
        for (const photo of photos) {
            if (cursorX > x && cursorX + photo.width > x + maxWidth) {
                cursorX = x;
                rowY += rowMaxHeight + gap;
                rowMaxHeight = 0;
            }
            const dataUri = CommonFileUtils.getImageDataUri(photo.thumb);
            const captionName = this.truncateText(photo.creatorName, 28);
            const captionDate = photo.createdDate;
            parts.push(`<image href="${dataUri}" x="${cursorX}" y="${rowY}" width="${photo.width}" height="${photo.height}" preserveAspectRatio="xMidYMid meet"/>
  <text x="${cursorX}" y="${rowY + photo.height + captionLineHeight}" font-family="${this.FONT}" font-size="10" fill="#222222">${this.escapeXml(captionName)}</text>
  <text x="${cursorX}" y="${rowY + photo.height + captionLineHeight * 2}" font-family="${this.FONT}" font-size="10" fill="#606060">${this.escapeXml(captionDate)}</text>`);
            cursorX += photo.width + gap;
            rowMaxHeight = Math.max(rowMaxHeight, photo.height + captionBlockHeight);
        }
        return parts.join('\n  ');
    }
    static truncateText(value, maxChars) {
        if (!value) {
            return '';
        }
        if (value.length <= maxChars) {
            return value;
        }
        return `${value.slice(0, Math.max(1, maxChars - 1)).trimEnd()}...`;
    }
    static renderAttachmentThumbnail(file, x, y) {
        const thumb = file.thumb;
        const border = `<rect x="${x}" y="${y}" width="${this.ATTACHMENT_THUMB_SIZE}" height="${this.ATTACHMENT_THUMB_SIZE}" rx="2" ry="2" fill="#f6f6f6" stroke="#d0d0d0" stroke-width="1"/>`;
        if (!thumb) {
            const fallbackLabel = this.getFileExtensionLabel(file.name);
            return `${border}
  <text x="${x + this.ATTACHMENT_THUMB_SIZE / 2}" y="${y + this.ATTACHMENT_THUMB_SIZE / 2 + 4}" text-anchor="middle" font-family="${this.FONT}" font-size="8" fill="#7a7a7a">${this.escapeXml(fallbackLabel)}</text>`;
        }
        const dataUri = CommonFileUtils.getImageDataUri(thumb);
        return `${border}
  <image href="${dataUri}" x="${x + 1}" y="${y + 1}" width="${this.ATTACHMENT_THUMB_SIZE - 2}" height="${this.ATTACHMENT_THUMB_SIZE - 2}" preserveAspectRatio="xMidYMid slice"/>`;
    }
    static getFileExtensionLabel(fileName) {
        if (!fileName) {
            return 'FILE';
        }
        const lastDot = fileName.lastIndexOf('.');
        if (lastDot < 0 || lastDot === fileName.length - 1) {
            return 'FILE';
        }
        return fileName
            .substring(lastDot + 1)
            .toUpperCase()
            .slice(0, 4);
    }
    static formatPersonWithCompany(people, personId) {
        const person = this.findPerson(people, personId);
        if (!person) {
            return '';
        }
        const name = this.getFullName(person);
        return person.company ? `${name} (${person.company})` : name;
    }
    static formatPersonCompany(people, personId) {
        const person = this.findPerson(people, personId);
        return person?.company ?? '';
    }
    static formatPeopleList(people, personIds) {
        return (personIds ?? [])
            .map((personId) => this.formatPersonWithCompany(people, personId))
            .filter(Boolean)
            .join(', ');
    }
    static wrapText(text, maxWidth, fontSize) {
        if (!text) {
            return [];
        }
        const charsPerLine = Math.max(12, Math.floor(maxWidth / (fontSize * 0.55)));
        const paragraphs = text.split(/\r?\n/);
        const lines = [];
        for (const paragraph of paragraphs) {
            const words = paragraph.split(/\s+/).filter(Boolean);
            if (words.length === 0) {
                lines.push('');
                continue;
            }
            let current = words[0];
            for (let i = 1; i < words.length; i++) {
                const candidate = `${current} ${words[i]}`;
                if (candidate.length <= charsPerLine) {
                    current = candidate;
                }
                else {
                    lines.push(current);
                    current = words[i];
                }
            }
            lines.push(current);
        }
        return lines;
    }
    static escapeXml(text) {
        if (!text) {
            return '';
        }
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    static findPerson(people, personId) {
        return people.find((person) => person._id === personId) ?? null;
    }
    static getFullName(person) {
        return `${person.first_name} ${person.last_name}`;
    }
}
